import {
  getGetHealthDataProfileV1HealthDataGetQueryKey,
  getGetQuestionDetailsV1HealthDataSlugQuestionDetailsGetQueryKey,
  useGetHealthDataProfileV1HealthDataGet,
  useSubmitHealthDataProfileV1HealthDataProfilePost,
  useUpdateHealthDataProfileV1HealthDataSlugPut,
} from '@/api/generated-catch-backend/health-data/health-data';
import {
  Block,
  HealthDataCategory,
  HealthDataProfileResponse,
  QuestionnaireCategoryItemsItemOneOf,
  SubmitHealthDataProfileResponse,
} from '@/api/generated-catch-backend/model';
import { formatAnswerForSubmission } from '@/app/services/questionnaireV2.service';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

export interface MappedCategory {
  name: string;
  description: Block[] | null;
  icon: string;
  progressValue: number;
  progressMax: number;
  lastUpdated: string;
  isComplete: boolean;
  subcategories?: HealthDataCategory['subcategories'];
  order: number;
}

/**
 * Maps a HealthDataCategory from the backend to the format expected by UI components
 */
export function mapCategoryForUI(category: HealthDataCategory): MappedCategory {
  return {
    name: category.name,
    description: category.description ?? null,
    icon: category.icon?.url ?? '',
    progressValue: category.populatedDataPoints,
    progressMax: category.totalDataPoints,
    lastUpdated: category.lastUpdated,
    isComplete: category.populatedDataPoints === category.totalDataPoints,
    subcategories: category.subcategories,
    order: category.displayOrder,
  };
}

/**
 * Custom hook for fetching and managing health data profile.
 *
 * **Caching Strategy:**
 * - Data is cached for 5 minutes (staleTime) - no re-fetches during this period
 * - Cache persists for 10 minutes (gcTime) after last component unmount
 * - Data is shared across all components using this hook
 * - Navigating between pages won't trigger new API calls if data is fresh
 * - Data is automatically revalidated when mutations succeed
 *
 * @param param0.includeHistory - whether to include the health data history (currently unused by API)
 * @returns Health data profile, mapped categories, and update mutation
 */
export const useGetHealthData = (): {
  isFetching: boolean;
  isInitiallyFetching: boolean;
  isError: boolean;
  healthData?: HealthDataProfileResponse;
  mappedCategories: MappedCategory[];
  updateHealthDataPoint: ReturnType<typeof useUpdateHealthDataProfileV1HealthDataSlugPut>;
} => {
  const request = useGetHealthDataProfileV1HealthDataGet({
    query: {
      // Cache data for 5 minutes - won't refetch during this time
      staleTime: 5 * 60 * 1000,
      // Keep data in cache for 10 minutes after last use
      gcTime: 10 * 60 * 1000,
      // Don't refetch on window focus - data is stable
      refetchOnWindowFocus: false,
      // Don't refetch on mount if data exists in cache
      refetchOnMount: false,
      // Keep previous data while fetching to prevent CLS
      placeholderData: (previousData) => previousData,
    },
  });
  const queryClient = useQueryClient();
  const updateHealthDataPoint = useUpdateHealthDataProfileV1HealthDataSlugPut({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetHealthDataProfileV1HealthDataGetQueryKey() });
      },
    },
  });

  const mappedCategories = useMemo(
    () => (request.data?.categories || []).map(mapCategoryForUI),
    [request.data?.categories],
  );

  return {
    // Original fetching state - true whenever data is being fetched
    isFetching: request.isFetching || updateHealthDataPoint.isPending,
    // For skeleton loader - only true on initial load, not on refetch with existing data
    isInitiallyFetching: request.isLoading || (request.isFetching && !request.data),
    isError: request.isError || updateHealthDataPoint.isError,
    healthData: request.data,
    mappedCategories,
    updateHealthDataPoint,
  };
};

/**
 * Hook for updating a health data point with proper formatting and cache invalidation.
 *
 * @returns An object with updateHealthDataPoint function and mutation state
 */
export const useUpdateHealthDataPoint = () => {
  const queryClient = useQueryClient();
  const mutation = useUpdateHealthDataProfileV1HealthDataSlugPut();

  const updateHealthDataPoint = useCallback(
    async ({
      healthDataPointSlug,
      value,
      question,
      onSuccess,
      onError,
    }: {
      healthDataPointSlug: string;
      value:
        | string
        | number
        | boolean
        | Record<string, string>
        | Record<string, string | number>
        | Record<string, Record<string, number>>
        | string[];
      question: QuestionnaireCategoryItemsItemOneOf;
      onSuccess?: () => void;
      onError?: (error: any) => void;
    }) => {
      // Validate value
      if (value === null || value === undefined) {
        console.error('No answer value provided');
        onError?.(new Error('No answer value provided'));
        return;
      }

      // Get units if applicable
      const units = question.questionType === 'number' && question.units ? question.units : null;

      // Format the answer using the existing service function
      const formattedAnswer = formatAnswerForSubmission({
        value,
        questionType: question.questionType,
        units,
      });

      if (!formattedAnswer) {
        console.error('Failed to format answer for submission');
        onError?.(new Error('Failed to format answer for submission'));
        return;
      }

      // Execute the mutation
      mutation.mutate(
        {
          slug: healthDataPointSlug,
          data: {
            value: formattedAnswer.value,
            questionSlug: question.slug,
          },
        },
        {
          onSuccess: async () => {
            // Invalidate health data profile query to refetch latest data
            await queryClient.invalidateQueries({
              queryKey: getGetHealthDataProfileV1HealthDataGetQueryKey(),
            });
            // Invalidate the specific question details query for this health data point
            await queryClient.invalidateQueries({
              queryKey: getGetQuestionDetailsV1HealthDataSlugQuestionDetailsGetQueryKey(healthDataPointSlug),
            });
            onSuccess?.();
          },
          onError: (error) => {
            console.error('Failed to update health data:', error);
            onError?.(error);
          },
        },
      );
    },
    [mutation, queryClient],
  );

  return {
    updateHealthDataPoint,
    isPending: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  };
};

/**
 * Hook for recalculating a user's risk score by submitting the in-progress questionnaire
 * and checking for risk impact changes.
 *
 * 1. Submits the in-progress questionnaire response (triggers risk calculation)
 * 2. Compares the new risk calculation with the previous one
 * 3. Returns risk impact changes by cancer type (e.g., "Breast cancer decreased by 1.5%")
 *
 * The health data profile will be automatically refetched via the mutation's onSuccess callback.
 *
 * @returns An object with recalculate function, mutation state, and risk impact data
 */
export const useRecalculateRiskScore = () => {
  const queryClient = useQueryClient();

  // Use the new submit endpoint that handles both submission and risk comparison
  const submitMutation = useSubmitHealthDataProfileV1HealthDataProfilePost({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: getGetHealthDataProfileV1HealthDataGetQueryKey(),
        });
      },
    },
  });

  const recalculate = useCallback(
    (options?: { onSuccess?: (data: SubmitHealthDataProfileResponse) => void; onError?: (error: unknown) => void }) => {
      // Submit the in-progress questionnaire and get risk impact changes
      submitMutation.mutate(undefined, {
        onSuccess: (data) => {
          options?.onSuccess?.(data);
        },
        onError: (error) => {
          console.error('Failed to recalculate risk score:', error);
          options?.onError?.(error);
        },
      });
    },
    [submitMutation],
  );

  return {
    recalculate,
    isRecalculating: submitMutation.isPending,
    isError: submitMutation.isError,
    error: submitMutation.error,
    riskImpactData: submitMutation.data,
    hasResponse: true,
  };
};
